import React, { useState, useEffect, useRef } from 'react';
import API_URL from '../../../config';
import DashboardLayout from '../../layouts/DashboardLayout';
import Button from '../../components/ui/Button';
import { useAuth } from '../../../context/AuthContext';
import { AdminService } from '../../../application/services/AdminService';
import { useToast } from '../../../context/ToastContext';
import { User, CreditCard, MapPin, Phone, Lock, Hash, Camera } from 'lucide-react';


const PerfilPage = () => {
    const { user, updateUserData } = useAuth();
    const [formData, setFormData] = useState({
        nombre: '',
        cedula: '',
        direccion: '',
        telefono: '',
        usuario: '',
        contrasenia: '',
        rol: '' // Solo lectura
    });
    const [previewImage, setPreviewImage] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    
    // Mapeo de roles para mostrar nombre completo
    const resolveRoleName = (roleCode) => {
        const roleMap = {
            'A': 'Administrador',
            'P': 'Presidente',
            'T': 'Tesorero',
            'S': 'Secretario',
            'L': 'Lecturador',
            'R': 'Super Admin',
            'O': 'Operador'
        };
        // Si es un objeto rol, o un string
        const code = typeof roleCode === 'object' ? roleCode?.nombre : roleCode;
        return roleMap[code] || code || 'Usuario';
    };

    const fileInputRef = useRef(null);
    const toast = useToast();

    useEffect(() => {
        if (user) {
            const rawRole = Array.isArray(user.roles) ? user.roles[0] : (user.rol || '');
            
            setFormData({
                nombre: user.nombre || '',
                cedula: user.cedula || '',
                direccion: user.direccion || '',
                telefono: user.telefono || '',
                usuario: user.usuario || '',
                rol: resolveRoleName(rawRole),
                contrasenia: ''
            });

            if (user.foto) {
                setPreviewImage(`${API_URL}${user.foto}`);
            }
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('La imagen no puede superar los 5MB');
                return;
            }
            setSelectedFile(file);
            // Crear preview
            const objectUrl = URL.createObjectURL(file);
            setPreviewImage(objectUrl);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const userId = user.id_usuario || user.id;

        if (!userId) {
            toast.error('Error: No se puede identificar al usuario. Recargue la página.');
            return;
        }

        try {
            // Usamos FormData para enviar archivo
            const data = new FormData();
            data.append('nombre', formData.nombre);
            data.append('cedula', formData.cedula);
            data.append('direccion', formData.direccion);
            data.append('telefono', formData.telefono);
            data.append('usuario', formData.usuario);
            
            // Solo enviar password si se escribió algo
            if (formData.contrasenia) {
                data.append('password', formData.contrasenia);
            }
            
            if (selectedFile) {
                data.append('foto', selectedFile);
            }

            // Mantenemos el ID de empresa y roles del usuario actual para no perderlos, 
            // aunque el backend debería manejarlos con cuidado.
            if (user.id_empresa) data.append('id_empresa', user.id_empresa);

            // AdminService debe soportar FormData o axios lo detecta
            // Nota: AdminService.updateUsuario usa json por defecto, 
            // pero axios detecta FormData y ajusta headers 'Content-Type': 'multipart/form-data'
            
            const updatedUser = await AdminService.updateUsuario(userId, data);
            
            toast.success('Perfil actualizado correctamente');
            
            // Usamos el nuevo método del context para actualizar el estado global y localStorage
            updateUserData(updatedUser);
            
            // Opcional: Si cambió la contraseña o el username de tal forma que invalida el token (no suele pasar con JWT basic)
            // podríamos redirigir, pero si solo cambiaron datos personales, basta con actualizar el estado.
            // Para asegurar que todos los componentes (como Sidebar) se actualicen, 
            // no necesitamos reload si usamos correctamente el context.
            
            setTimeout(() => {
                window.location.reload(); // Recarga controlada para asegurar efectos visuales (foto, sidebar)
            }, 1000);

        } catch (error) {
            console.error(error);
            toast.error(error.message || 'Error al actualizar perfil');
        } finally {
            setLoading(false);
        }
    };
    return (
        <DashboardLayout title="Mi Perfil">
            <div className="w-full animate-fade-in">
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">Información Personal</h2>
                            <p className="text-sm text-gray-500">Actualiza tus datos personales y de acceso.</p>
                        </div>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="p-6 space-y-8">
                        
                        {/* Sección de Foto */}
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <div className="relative group cursor-pointer" onClick={handleImageClick}>
                                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-100 shadow-md transform transition-transform group-hover:scale-105">
                                    {previewImage ? (
                                        <img src={previewImage} alt="Perfil" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full bg-blue-100 flex items-center justify-center text-blue-500 text-4xl font-bold">
                                            {formData.nombre?.charAt(0) || 'U'}
                                        </div>
                                    )}
                                </div>
                                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Camera className="w-8 h-8 text-white" />
                                </div>
                                <input 
                                    type="file" 
                                    ref={fileInputRef} 
                                    className="hidden" 
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                            </div>
                            <p className="text-xs text-gray-500">Click para cambiar foto (Máx 5MB)</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Nombre */}
                            <div className="space-y-2">
                                <label className="flex items-center text-sm font-medium text-gray-700 gap-2">
                                    <User className="w-4 h-4 text-gray-400" />
                                    Nombre Completo
                                </label>
                                <input
                                    type="text"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                    required
                                />
                            </div>

                            {/* Cédula */}
                            <div className="space-y-2">
                                <label className="flex items-center text-sm font-medium text-gray-700 gap-2">
                                    <CreditCard className="w-4 h-4 text-gray-400" />
                                    Cédula / DNI
                                </label>
                                <input
                                    type="text"
                                    name="cedula"
                                    value={formData.cedula}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                    required
                                />
                            </div>

                            {/* Dirección */}
                            <div className="space-y-2">
                                <label className="flex items-center text-sm font-medium text-gray-700 gap-2">
                                    <MapPin className="w-4 h-4 text-gray-400" />
                                    Dirección
                                </label>
                                <input
                                    type="text"
                                    name="direccion"
                                    value={formData.direccion}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                />
                            </div>

                            {/* Teléfono */}
                            <div className="space-y-2">
                                <label className="flex items-center text-sm font-medium text-gray-700 gap-2">
                                    <Phone className="w-4 h-4 text-gray-400" />
                                    Teléfono
                                </label>
                                <input
                                    type="text"
                                    name="telefono"
                                    value={formData.telefono}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                />
                            </div>

                            {/* Usuario */}
                            <div className="space-y-2">
                                <label className="flex items-center text-sm font-medium text-gray-700 gap-2">
                                    <User className="w-4 h-4 text-gray-400" />
                                    Nombre de Usuario
                                </label>
                                <input
                                    type="text"
                                    name="usuario"
                                    value={formData.usuario}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                    required
                                />
                            </div>

                            {/* Rol (Read Only) */}
                            <div className="space-y-2">
                                <label className="flex items-center text-sm font-medium text-gray-700 gap-2">
                                    <Hash className="w-4 h-4 text-gray-400" />
                                    Rol
                                </label>
                                <input
                                    type="text"
                                    value={formData.rol}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                                />
                            </div>

                            {/* Contraseña */}
                            <div className="col-span-1 md:col-span-2 space-y-2">
                                <label className="flex items-center text-sm font-medium text-gray-700 gap-2">
                                    <Lock className="w-4 h-4 text-gray-400" />
                                    Nueva Contraseña
                                </label>
                                <input
                                    type="password"
                                    name="contrasenia"
                                    value={formData.contrasenia}
                                    onChange={handleChange}
                                    placeholder="Dejar en blanco para mantener la actual"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                />
                                <p className="text-xs text-gray-500">Solo llena este campo si deseas cambiar tu contraseña.</p>
                            </div>
                        </div>

                        <div className="pt-4 flex justify-end gap-3">
                            <Button 
                                type="button"
                                variant="outline"
                                onClick={() => window.history.back()}
                            >
                                Cancelar
                            </Button>
                            <Button 
                                type="submit" 
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 w-full sm:w-auto"
                                disabled={loading}
                            >
                                {loading ? 'Guardando...' : 'Guardar Cambios'}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default PerfilPage;
